import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { FileText } from 'lucide-react';

const Terms = () => {
  const lastUpdated = 'March 2, 2026';

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
                <FileText className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">Terms and Conditions</h1>
              <p className="text-muted-foreground">Last Updated: {lastUpdated}</p>
            </div>

            {/* Content */}
            <div className="bg-white rounded-3xl p-8 md:p-12 border-2 border-border prose prose-lg max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-heading font-bold mb-4">1. Agreement to Terms</h2>
                <ul className="list-disc pl-6 text-muted-foreground">
                  <li>By accessing or using ExpatsSphere (the "Platform"), you agree to be bound by these Terms and Conditions.</li>
                  <li>These Terms constitute a legally binding agreement between you and ExpatsSphere.</li>
                  <li>If you do not agree with any part of these Terms, you must immediately discontinue use of the Platform.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-bold mb-4">2. Eligibility</h2>
                <ul className="list-disc pl-6 text-muted-foreground">
                  <li>You must be at least 18 years of age to create an account and use the services provided by ExpatsSphere.</li>
                  <li>By registering, you represent and warrant that you meet this age requirement and have the legal capacity to enter into this agreement.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-bold mb-4">3. User Conduct & Community Standards</h2>
                <p className="text-muted-foreground mb-4">
                  To maintain a safe and welcoming environment for all expats, you agree to:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground">
                  <li>Provide accurate and truthful information during registration.</li>
                  <li>Abstain from harassment, bullying, stalking, or the posting of hate speech.</li>
                  <li>Avoid using the Platform for unsolicited commercial "spam," "get rich quick" schemes, or unauthorized advertising.</li>
                  <li>Respect the cultural backgrounds, privacy, and personal boundaries of all members.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-bold mb-4">4. Objectionable Content Policy</h2>
                <p className="text-muted-foreground mb-4">
                  ExpatsSphere has zero tolerance for objectionable content or abusive users. The following content is strictly prohibited:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground mb-4">
                  <li>Content that is offensive, insensitive, upsetting, intended to disgust, or in exceptionally poor taste.</li>
                  <li>Sexually explicit or pornographic material.</li>
                  <li>Defamatory, discriminatory, or mean-spirited content, including references or commentary about religion, race, sexual orientation, gender, or other targeted groups.</li>
                  <li>Realistic depictions of people or animals being killed, maimed, tortured, or abused, or content that encourages violence.</li>
                  <li>Content that promotes or facilitates illegal activities.</li>
                  <li>Spam, phishing, or malicious links.</li>
                </ul>
                <p className="text-muted-foreground mb-4">
                  <strong>Content Filtering:</strong> ExpatsSphere employs automated content filtering to detect and prevent objectionable content from being published. Content that violates these guidelines will be automatically blocked or flagged for review.
                </p>
                <p className="text-muted-foreground mb-4">
                  <strong>Reporting:</strong> Users may report any objectionable content or abusive behavior by using the Report feature available on all user profiles, events, and content. Reports are reviewed by our moderation team within 24 hours.
                </p>
                <p className="text-muted-foreground mb-4">
                  <strong>Blocking:</strong> Users may block other users at any time. Blocking a user will hide their content from your view and prevent them from contacting you. Blocks are also logged for admin review.
                </p>
                <p className="text-muted-foreground mb-4">
                  <strong>Enforcement:</strong> ExpatsSphere reserves the right to remove any objectionable content and eject users who violate these guidelines, without prior notice. Accounts that repeatedly post objectionable content will be permanently suspended.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-bold mb-4">5. Events & In-Person Interaction (Liability Waiver)</h2>
                <p className="text-muted-foreground mb-4">
                  ExpatsSphere facilitates online connections that may lead to in-person meetings and events.
                </p>
                <p className="text-muted-foreground mb-2"><strong>Assumption of Risk:</strong></p>
                <ul className="list-disc pl-6 text-muted-foreground mb-4">
                  <li>You acknowledge that ExpatsSphere does not conduct criminal background checks or identity verification beyond email confirmation.</li>
                  <li>Your participation in any in-person event or meeting is strictly at your own risk.</li>
                </ul>
                <p className="text-muted-foreground mb-2"><strong>Limitation of Liability:</strong></p>
                <ul className="list-disc pl-6 text-muted-foreground">
                  <li>ExpatsSphere, its founders, and employees shall not be held liable for any injury, loss, harm, or damage resulting from offline interactions or events organized through the Platform.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-bold mb-4">6. Subscriptions and Payments</h2>
                <p className="text-muted-foreground mb-2"><strong>Promotional Period:</strong></p>
                <ul className="list-disc pl-6 text-muted-foreground mb-4">
                  <li>Membership is currently offered Free for two (2) years from your signup date.</li>
                  <li>No credit card is required for this period.</li>
                </ul>
                <p className="text-muted-foreground mb-2"><strong>Future Billing:</strong></p>
                <ul className="list-disc pl-6 text-muted-foreground">
                  <li>ExpatsSphere reserves the right to introduce paid tiers or subscription fees.</li>
                  <li>Any transition to a paid model will require your explicit consent and the provision of payment details via our secure third-party processor, Stripe.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-bold mb-4">7. Content and Intellectual Property</h2>
                <p className="text-muted-foreground mb-2"><strong>User Content:</strong></p>
                <ul className="list-disc pl-6 text-muted-foreground mb-4">
                  <li>You retain ownership of the photos and text you post.</li>
                  <li>By posting, you grant ExpatsSphere a non-exclusive, royalty-free license to display your content to other members.</li>
                </ul>
                <p className="text-muted-foreground mb-2"><strong>Platform Property:</strong></p>
                <ul className="list-disc pl-6 text-muted-foreground">
                  <li>The ExpatsSphere name, logo, branding, and software code are the exclusive property of ExpatsSphere and are protected by copyright and trademark laws.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-bold mb-4">8. Account Termination</h2>
                <p className="text-muted-foreground mb-2"><strong>By You:</strong></p>
                <ul className="list-disc pl-6 text-muted-foreground mb-4">
                  <li>You may delete your account at any time via the app settings.</li>
                </ul>
                <p className="text-muted-foreground mb-2"><strong>By Us:</strong></p>
                <ul className="list-disc pl-6 text-muted-foreground">
                  <li>We reserve the right to suspend or terminate your account at our sole discretion, without notice, for conduct that violates these Terms or is deemed harmful to the community.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-bold mb-4">9. Disclaimer of Warranties</h2>
                <ul className="list-disc pl-6 text-muted-foreground">
                  <li>The Platform is provided on an "as-is" and "as-available" basis.</li>
                  <li>ExpatsSphere makes no warranties, expressed or implied, regarding the reliability or accuracy of the service or the behavior of its users.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-bold mb-4">10. Governing Law</h2>
                <ul className="list-disc pl-6 text-muted-foreground">
                  <li>These Terms are governed by and construed in accordance with the laws of the United States, without regard to its conflict of law principles.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-heading font-bold mb-4">11. Contact Us</h2>
                <p className="text-muted-foreground mb-4">
                  For any legal inquiries or questions regarding these Terms, please contact:
                </p>
                <p className="text-muted-foreground">
                  <strong>Email:</strong> Support@expatssphere.com<br />
                  <strong>Web:</strong> expatssphere.com/contact
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Terms;
